import React from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
const sections = [{
  id: 'overview',
  title: 'Overview',
  content: <>
        <p data-id="element-278">
          This Privacy Policy describes how Alejandro Noriega Photography
          (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;) collects, uses, and protects
          information when you visit{' '}
          <span className="text-charcoal font-medium" data-id="element-279">alejandronoriega.art</span>{' '}
          or interact with us through Instagram or email.
        </p>
        <p className="mt-4" data-id="element-280">
          We are committed to handling your data with care and transparency.
          This policy covers our website analytics, newsletter subscription,
          Instagram automation tools, and direct communications.
        </p>
      </>
}, {
  id: 'data-collected',
  title: 'Data We Collect',
  content: <>
        <p className="mb-4" data-id="element-281">We collect data through the following channels:</p>
        <div className="space-y-6" data-id="element-282">
          <div data-id="element-283">
            <p className="text-charcoal font-medium text-sm mb-2 tracking-wide" data-id="element-284">Website visitors</p>
            <p data-id="element-285">
              When you browse our site, we automatically collect anonymised
              usage data including pages visited, photos clicked, time spent on
              each photo, device type, browser, and approximate geographic
              location (country/city level). This is collected via PostHog
              analytics and does not identify you personally.
            </p>
          </div>
          <div data-id="element-286">
            <p className="text-charcoal font-medium text-sm mb-2 tracking-wide" data-id="element-287">Newsletter subscription</p>
            <p data-id="element-288">
              If you voluntarily subscribe to our newsletter, we collect your
              email address, the date of subscription, and the source (website).
              Subscription is entirely optional and based on your explicit
              consent.
            </p>
          </div>
          <div data-id="element-289">
            <p className="text-charcoal font-medium text-sm mb-2 tracking-wide" data-id="element-290">Instagram interactions</p>
            <p data-id="element-291">
              We access public comments made on our Instagram account
              (@alenoriegaes) through the Meta Graph API. This includes comment
              text and the commenter&apos;s public Instagram username. We do not
              access private profile information.
            </p>
          </div>
          <div data-id="element-292">
            <p className="text-charcoal font-medium text-sm mb-2 tracking-wide" data-id="element-293">Direct email contact</p>
            <p data-id="element-294">
              When you contact us by email, we receive your email address and
              any information you choose to include in your message. We use this
              solely to respond to your inquiry.
            </p>
          </div>
        </div>
      </>
}, {
  id: 'analytics',
  title: 'Analytics & Tracking',
  content: <>
        <p className="mb-4" data-id="element-295">
          We use{' '}
          <a href="https://posthog.com" target="_blank" rel="noopener noreferrer" className="text-charcoal underline underline-offset-4 hover:opacity-60 transition-opacity" data-id="element-296">
            PostHog
          </a>{' '}
          to understand how visitors interact with our site. The data we track
          includes:
        </p>
        <ul className="space-y-2 ml-4" data-id="element-297">
          {['Pages viewed and navigation paths', 'Photos clicked and detail pages viewed', 'Print size selections', 'WhatsApp inquiry button clicks', 'Responses to optional exit surveys', 'Session duration and referral source', 'Device type, browser, and operating system', 'Approximate location (country and city — not precise address)'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-298">
              <span className="mt-2 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" data-id="element-299" />
              <span data-id="element-300">{item}</span>
            </li>)}
        </ul>
        <p className="mt-6" data-id="element-301">
          This data is used exclusively to improve the site experience and
          understand which work resonates with our audience. We do not use it
          for advertising targeting. PostHog data is stored on servers in the
          United States.
        </p>
        <p className="mt-4" data-id="element-302">
          To opt out of analytics tracking, you can use a browser extension
          such as uBlock Origin, or contact us and we will manually exclude
          your session.
        </p>
      </>
}, {
  id: 'cookies',
  title: 'Cookies',
  content: <>
        <p className="mb-4" data-id="element-303">
          Our site uses cookies and local storage to function correctly and to
          collect analytics data. Specifically:
        </p>
        <div className="space-y-4" data-id="element-304">
          <div className="border-l-2 border-gray-200 pl-4" data-id="element-305">
            <p className="text-charcoal font-medium text-sm mb-1" data-id="element-306">Analytics cookies (PostHog)</p>
            <p data-id="element-307">
              Used to identify returning visitors and maintain session
              continuity for analytics purposes. These cookies do not contain
              personally identifiable information.
            </p>
          </div>
          <div className="border-l-2 border-gray-200 pl-4" data-id="element-308">
            <p className="text-charcoal font-medium text-sm mb-1" data-id="element-309">Functional local storage</p>
            <p data-id="element-310">
              We store minimal data locally (such as comment IDs for Instagram
              automation) to prevent sending duplicate messages. This data is
              not transmitted to third parties.
            </p>
          </div>
        </div>
        <p className="mt-6" data-id="element-311">
          You can disable cookies through your browser settings. Note that
          disabling cookies may affect how the site functions. By continuing to
          use this site, you consent to the use of cookies as described above.
        </p>
      </>
}, {
  id: 'newsletter',
  title: 'Newsletter Subscription',
  content: <>
        <p className="mb-4" data-id="element-312">
          When you subscribe to our newsletter, your email address is stored
          securely in a private Google Sheet managed by Alejandro Noriega
          Photography. We use this list to send occasional updates about new
          photographic series, limited edition prints, and exhibitions.
        </p>
        <p data-id="element-313">
          We will never share your email address with third parties or use it
          for any purpose other than the newsletter you subscribed to. You can
          unsubscribe at any time by contacting us at{' '}
          <a href="mailto:alejandron@gmail.com" className="text-charcoal underline underline-offset-4 hover:opacity-60 transition-opacity" data-id="element-314">
            alejandron@gmail.com
          </a>{' '}
          and your email will be removed within 48 hours.
        </p>
      </>
}, {
  id: 'instagram-automation',
  title: 'Instagram Automation',
  content: <>
        <p className="mb-4" data-id="element-315">
          We operate an automated Direct Message system on Instagram. When a
          user comments a specific keyword on one of our posts, our system
          sends them a single automated DM with information about available
          photography prints.
        </p>
        <p className="mb-4" data-id="element-316">
          This system accesses public comments through the Meta (Instagram)
          Graph API. It reads comment text and the commenter&apos;s public
          username. Comment IDs are stored temporarily to prevent sending
          duplicate messages. No message content, personal profile data, or
          follower information is retained.
        </p>
        <p data-id="element-317">
          This functionality is operated in compliance with Meta&apos;s Platform
          Terms of Service and Messaging Policy. For questions about how Meta
          handles your data, please refer to{' '}
          <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="text-charcoal underline underline-offset-4 hover:opacity-60 transition-opacity" data-id="element-318">
            Meta&apos;s Privacy Policy
          </a>
          .
        </p>
      </>
}, {
  id: 'third-parties',
  title: 'Third-Party Services',
  content: <>
        <p className="mb-4" data-id="element-319">
          We use the following third-party services. Each operates under its
          own privacy policy:
        </p>
        <div className="space-y-4" data-id="element-320">
          {[{
        name: 'PostHog',
        url: 'https://posthog.com/privacy',
        description: 'Website analytics and event tracking.'
      }, {
        name: 'Meta (Instagram Graph API)',
        url: 'https://www.facebook.com/privacy/policy/',
        description: 'Reading public Instagram comments and sending automated Direct Messages.'
      }, {
        name: 'Google Sheets / Apps Script',
        url: 'https://policies.google.com/privacy',
        description: 'Storage of newsletter subscriber email addresses.'
      }].map(service => <div key={service.name} className="flex gap-4" data-id="element-321">
              <div className="w-1 flex-shrink-0 bg-gray-200 rounded" data-id="element-322" />
              <div data-id="element-323">
                <a href={service.url} target="_blank" rel="noopener noreferrer" className="text-charcoal font-medium text-sm underline underline-offset-4 hover:opacity-60 transition-opacity" data-id="element-324">
                  {service.name}
                </a>
                <p className="mt-1" data-id="element-325">{service.description}</p>
              </div>
            </div>)}
        </div>
        <p className="mt-6" data-id="element-326">
          We do not sell, rent, or share personal data with any third party
          beyond what is described above.
        </p>
      </>
}, {
  id: 'retention',
  title: 'Data Retention',
  content: <>
        <p className="mb-4" data-id="element-327">
          We retain data only for as long as necessary:
        </p>
        <ul className="space-y-2 ml-4" data-id="element-328">
          {['Analytics data (PostHog): retained for up to 12 months.', 'Newsletter email addresses: retained until you unsubscribe.', 'Instagram comment IDs: retained during active automation sessions only.', 'Email correspondence: retained for as long as relevant to the inquiry.'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-329">
              <span className="mt-2 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" data-id="element-330" />
              <span data-id="element-331">{item}</span>
            </li>)}
        </ul>
      </>
}, {
  id: 'your-rights',
  title: 'Your Rights',
  content: <>
        <p className="mb-4" data-id="element-332">
          Depending on your location, you may have the following rights
          regarding your personal data. Visitors from the European Union and
          Spain are covered under the General Data Protection Regulation
          (GDPR):
        </p>
        <ul className="space-y-2 ml-4" data-id="element-333">
          {['Right to access: request a copy of the data we hold about you.', 'Right to rectification: request correction of inaccurate data.', 'Right to erasure: request deletion of your personal data.', 'Right to object: object to certain types of processing.', 'Right to withdraw consent: unsubscribe from the newsletter at any time.'].map(item => <li key={item} className="flex items-start gap-3" data-id="element-334">
              <span className="mt-2 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" data-id="element-335" />
              <span data-id="element-336">{item}</span>
            </li>)}
        </ul>
        <p className="mt-6" data-id="element-337">
          To exercise any of these rights, contact us at{' '}
          <a href="mailto:alejandron@gmail.com" className="text-charcoal underline underline-offset-4 hover:opacity-60 transition-opacity" data-id="element-338">
            alejandron@gmail.com
          </a>
          . We will respond within 30 days.
        </p>
      </>
}, {
  id: 'changes',
  title: 'Changes to This Policy',
  content: <p data-id="element-339">
        We may update this Privacy Policy from time to time. The &ldquo;Last
        updated&rdquo; date at the top of this page reflects the most recent
        revision. We encourage you to review this page periodically.
        Continued use of the site after any changes constitutes acceptance of
        the updated policy.
      </p>
}, {
  id: 'contact',
  title: 'Contact',
  content: <p data-id="element-340">
        For any privacy-related questions, data requests, or concerns, contact
        us at:{' '}
        <a href="mailto:alejandron@gmail.com" className="text-charcoal underline underline-offset-4 hover:opacity-60 transition-opacity" data-id="element-341">
          alejandron@gmail.com
        </a>
        .
      </p>
}];
export function PrivacyPage() {
  return <main className="min-h-screen bg-offwhite" data-id="element-342">
      <Navbar data-id="element-343" />
      <div className="pt-36 pb-24 px-6 md:px-12" data-id="element-344">
        <div className="max-w-2xl mx-auto" data-id="element-345">

          {/* Header */}
          <div className="mb-16" data-id="element-346">
            <p className="text-xs font-sans tracking-[0.2em] uppercase text-gray-400 mb-4" data-id="element-347">
              Legal
            </p>
            <h1 className="font-serif text-3xl md:text-4xl tracking-[0.08em] uppercase text-charcoal mb-4" data-id="element-348">
              Privacy Policy
            </h1>
            <p className="text-xs font-sans tracking-[0.12em] text-gray-400 uppercase" data-id="element-349">
              Last updated: April 2026
            </p>
          </div>

          {/* Table of contents */}
          <nav className="mb-16 p-6 bg-white border border-gray-100" data-id="element-350">
            <p className="text-xs font-sans tracking-[0.15em] uppercase text-gray-400 mb-4" data-id="element-351">
              Contents
            </p>
            <ol className="space-y-2" data-id="element-352">
              {sections.map((s, i) => <li key={s.id} data-id="element-353">
                  <a href={`#${s.id}`} className="text-sm font-sans text-gray-500 hover:text-charcoal transition-colors" data-id="element-354">
                    {i + 1}. {s.title}
                  </a>
                </li>)}
            </ol>
          </nav>

          {/* Sections */}
          <div className="space-y-16" data-id="element-355">
            {sections.map(s => <section key={s.id} id={s.id} data-id="element-356">
                <h2 className="font-serif text-base tracking-[0.15em] uppercase text-charcoal mb-5 pb-3 border-b border-gray-100" data-id="element-357">
                  {s.title}
                </h2>
                <div className="font-sans text-sm text-gray-600 leading-relaxed" data-id="element-358">
                  {s.content}
                </div>
              </section>)}
          </div>

          {/* Back to home */}
          <div className="mt-20 pt-8 border-t border-gray-200" data-id="element-359">
            <a href="/" className="text-xs font-sans tracking-[0.15em] uppercase text-gray-400 hover:text-charcoal transition-colors" data-id="element-360">
              &larr; Back to home
            </a>
          </div>

        </div>
      </div>
      <Footer data-id="element-361" />
    </main>;
}